import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  FormInstance,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  notification,
} from 'antd';
import { CopyOutlined, LoadingOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';

import {
  useAddCredits,
  useCreateClient,
} from 'client/modules/external-api/hooks/queries/external-api';

import {
  ApiApplication,
  Client,
  Dataset,
  IdPackageType,
} from '@cbinsights/externalapiservice/externalapiservice';
import { CheckboxOptionType } from 'antd/es/checkbox';
import { useQueryClient } from 'react-query';
import { dateFormat } from './constants';

import './CreateClient.css';

const oneYearFromNow = moment().add(1, 'years');

export const APIClientFormField = {
  packageType: 'packageType',
  includeV2API: 'includeV2API',
  expirationDate: 'expirationDate',
  credits: 'credits',
  datasets: 'datasets',
  application: 'application',
  srCreditsAllocation: 'srCreditsAllocation',
  srAllowedOverage: 'srAllowedOverage',
  chatCBICreditsAllocation: 'chatCBICreditsAllocation',
  chatCBIAllowedOverage: 'chatCBIAllowedOverage',
} as const;

type FieldTypes = {
  packageType: IdPackageType;
  includeV2API: boolean;
  expirationDate: Moment;
  credits: number;
  datasets: Dataset[];
  srCreditsAllocation: number;
  srAllowedOverage: number;
  application: ApiApplication;
  chatCBICreditsAllocation: number;
  chatCBIAllowedOverage: number;
};

export type APIClientForm = {
  [K in keyof typeof APIClientFormField]: FieldTypes[K];
};

export const datasetsOptionType: CheckboxOptionType[] = [
  { label: 'Firmographics', value: Dataset.FIRMOGRAPHICS, disabled: true },
  {
    label: 'Firmographics IDs',
    value: Dataset.FIRMOGRAPHICS_IDS,
    disabled: true,
  },
  { label: 'Financial transactions', value: Dataset.FINANCIAL_TRANSACTIONS },
  { label: 'Management and board', value: Dataset.MANAGEMENT_AND_BOARD },
  { label: 'Business relationships', value: Dataset.BUSINESS_RELATIONSHIPS },
  { label: 'Outlook', value: Dataset.OUTLOOK },
  { label: 'Scouting Reports', value: Dataset.SCOUTING_REPORTS },
  { label: 'ChatCBI', value: Dataset.CHAT_CBI },
  {
    label: 'Clay',
    value: Dataset.CLAY,
    disabled: true,
    style: { display: 'none' },
  },
  { label: 'Revenue', value: Dataset.REVENUE },
  { label: 'Strategy Map', value: Dataset.STRATEGY_MAP },
  { label: 'Stealth Companies', value: Dataset.STEALTH_COMPANIES },
];

export const PackageTypeRow = ({
  hideV1Options,
  disableV2Check,
}: {
  hideV1Options?: boolean;
  disableV2Check?: boolean;
}) => {
  const form = Form.useFormInstance();

  const clientPackageTypeId = form.getFieldValue(
    APIClientFormField.packageType
  );
  return clientPackageTypeId === IdPackageType.LEGACY_PER_REQUEST ? (
    <Row gutter={16} className="mt-2">
      <Col span={12} className="form-item-borderless">
        <Form.Item
          name={APIClientFormField.packageType}
          label="Package Type"
          style={{ marginBottom: 'none' }}
        >
          <Radio.Group className="mt-2">
            <Radio value={IdPackageType.LEGACY_PER_REQUEST}>Legacy</Radio>
          </Radio.Group>
        </Form.Item>
      </Col>
    </Row>
  ) : (
    <Row gutter={16} className="mt-2">
      {!hideV1Options && (
        <Col span={12} className="form-item-borderless">
          <h3>v1 API</h3>
          <Form.Item
            name={APIClientFormField.packageType}
            label="Package Type"
            style={{ marginBottom: 'none' }}
          >
            <Radio.Group className="mt-2">
              <Space direction="vertical">
                {clientPackageTypeId !== IdPackageType.LEGACY_PER_REQUEST && (
                  <>
                    <Radio value={IdPackageType.ESSENTIAL_PER_ORG}>
                      Essential
                    </Radio>
                    <Radio value={IdPackageType.EXPERT_PER_ORG}>Expert</Radio>
                  </>
                )}
              </Space>
            </Radio.Group>
          </Form.Item>
          <div className="my-2">
            <Button
              onClick={() =>
                form.setFieldValue(APIClientFormField.packageType, null)
              }
            >
              Unselect
            </Button>
          </div>
        </Col>
      )}
      <Col span={12}>
        <h3>v2 API</h3>
        <Form.Item
          name={APIClientFormField.includeV2API}
          valuePropName="checked"
          className="mt-9"
        >
          <Checkbox disabled={disableV2Check}>Include v2 API</Checkbox>
        </Form.Item>
      </Col>
    </Row>
  );
};

export const InputsRow = ({
  includeCredits = true,
  hasInitialCredits = false,
}: {
  includeCredits?: boolean;
  hasInitialCredits?: boolean;
}) => {
  return (
    <Row gutter={16} className="mt-2">
      <Col span={14}>
        {includeCredits && (
          <Form.Item
            name={APIClientFormField.credits}
            label="Team Credits"
            rules={[
              {
                required: true,
                message: 'Credits is required',
              },
            ]}
          >
            <InputNumber
              className="w-full"
              placeholder="Org. Credits"
              disabled={hasInitialCredits}
            />
          </Form.Item>
        )}

        <Form.Item
          name={APIClientFormField.expirationDate}
          label="Expiration Date"
          rules={[
            {
              required: true,
              message: 'Expiration Date is required',
            },
          ]}
        >
          <DatePicker className="w-full" format={dateFormat} />
        </Form.Item>
      </Col>
    </Row>
  );
};

export const ApplicationFormItem = ({
  disableApplication,
}: {
  disableApplication: boolean;
}) => {
  return (
    <Row gutter={16} className="mt-2">
      <Col span={14}>
        <Form.Item shouldUpdate className="m-0">
          {({ getFieldValue }) => {
            const hasPackageType = Boolean(
              getFieldValue(APIClientFormField.packageType)
            );
            return getFieldValue(APIClientFormField.includeV2API) ? (
              <Form.Item
                name={APIClientFormField.application}
                label="Application"
                required
              >
                <Select
                  defaultValue={ApiApplication.API_APPLICATION_UNSPECIFIED}
                  disabled={disableApplication}
                >
                  <Select.Option
                    value={ApiApplication.API_APPLICATION_UNSPECIFIED}
                  >
                    Default
                  </Select.Option>
                  <Select.Option
                    value={ApiApplication.API_APPLICATION_DATAFEEDS}
                    disabled={hasPackageType}
                  >
                    Data Feeds
                  </Select.Option>
                  <Select.Option
                    value={ApiApplication.API_APPLICATION_CLAY}
                    disabled={hasPackageType}
                  >
                    Clay
                  </Select.Option>
                </Select>
              </Form.Item>
            ) : null;
          }}
        </Form.Item>
      </Col>
    </Row>
  );
};

export const DatasetsRow = () => {
  return (
    <Row gutter={16} className="mt-2">
      <Col span={24} className="form-item-borderless">
        <Form.Item shouldUpdate>
          {({ getFieldValue }) => {
            const application = getFieldValue(APIClientFormField.application);
            return getFieldValue(APIClientFormField.includeV2API) ? (
              <Form.Item
                name={APIClientFormField.datasets}
                label="v2 Datasets"
                required
                rules={[
                  {
                    required: true,
                    message: 'At least one dataset is required',
                  },
                ]}
              >
                <Checkbox.Group
                  options={
                    application === ApiApplication.API_APPLICATION_CLAY
                      ? datasetsOptionType
                          .filter((opt) => opt.value === Dataset.CLAY)
                          .map((opt) => {
                            const { style, ...rest } = opt;
                            return rest;
                          })
                      : datasetsOptionType
                  }
                />
              </Form.Item>
            ) : null;
          }}
        </Form.Item>
      </Col>
    </Row>
  );
};

export const DatasetConfigRow = () => {
  return (
    <Form.Item
      shouldUpdate
      className="m-0 ![&_div.ant-form-item-control-input]:min-h-[0px]"
    >
      {({ getFieldValue }) => {
        const datasets: Dataset[] = getFieldValue('datasets');
        const hasScoutingReports = datasets?.includes(Dataset.SCOUTING_REPORTS);
        const hasChatCBI = datasets?.includes(Dataset.CHAT_CBI);
        if (hasScoutingReports || hasChatCBI) {
          return (
            <Row gutter={16} className="mt-2">
              <Col span={14}>
                <Form.Item label="Dataset Configuration">
                  {hasScoutingReports && (
                    <Form.Item label="Scouting Reports">
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name={APIClientFormField.srCreditsAllocation}
                            label="Credit allocation"
                            rules={[
                              {
                                required: true,
                                message: 'Credit allocation is required',
                              },
                            ]}
                          >
                            <InputNumber
                              placeholder="Enter a number"
                              type="'number'"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name={APIClientFormField.srAllowedOverage}
                            label="Allowed overage"
                            rules={[
                              {
                                required: true,
                                message: 'Allowed overage is required',
                              },
                            ]}
                          >
                            <InputNumber
                              placeholder="Enter a number"
                              type="'number'"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form.Item>
                  )}
                  {hasChatCBI && (
                    <Form.Item label="ChatCBI">
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name={APIClientFormField.chatCBICreditsAllocation}
                            label="Credit allocation"
                            rules={[
                              {
                                required: true,
                                message: 'Credit allocation is required',
                              },
                            ]}
                          >
                            <InputNumber
                              placeholder="Enter a number"
                              type="'number'"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name={APIClientFormField.chatCBIAllowedOverage}
                            label="Allowed overage"
                            rules={[
                              {
                                required: true,
                                message: 'Allowed overage is required',
                              },
                            ]}
                          >
                            <InputNumber
                              placeholder="Enter a number"
                              type="'number'"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form.Item>
                  )}
                </Form.Item>
              </Col>
            </Row>
          );
        }
        return null;
      }}
    </Form.Item>
  );
};

export const onFormClientValuesChange =
  (form: FormInstance<APIClientForm>) =>
  (changedValues: Partial<APIClientForm>) => {
    if (
      APIClientFormField.includeV2API in changedValues &&
      changedValues.includeV2API === true
    ) {
      form.setFieldsValue({
        [APIClientFormField.datasets]: [Dataset.FIRMOGRAPHICS],
        [APIClientFormField.application]:
          ApiApplication.API_APPLICATION_UNSPECIFIED,
      });
    }
    if (
      APIClientFormField.includeV2API in changedValues &&
      !changedValues.includeV2API
    ) {
      form.setFieldsValue({
        [APIClientFormField.datasets]: [],
        [APIClientFormField.srAllowedOverage]: null,
        [APIClientFormField.srCreditsAllocation]: null,
        [APIClientFormField.chatCBICreditsAllocation]: null,
        [APIClientFormField.chatCBIAllowedOverage]: null,
      });
    }
    if (
      APIClientFormField.datasets in changedValues &&
      !changedValues?.datasets?.includes(Dataset.SCOUTING_REPORTS)
    ) {
      form.setFieldsValue({
        [APIClientFormField.srAllowedOverage]: null,
        [APIClientFormField.srCreditsAllocation]: null,
      });
    }
    if (
      APIClientFormField.datasets in changedValues &&
      !changedValues?.datasets?.includes(Dataset.CHAT_CBI)
    ) {
      form.setFieldsValue({
        [APIClientFormField.chatCBICreditsAllocation]: null,
        [APIClientFormField.chatCBIAllowedOverage]: null,
      });
    }
    if (APIClientFormField.application in changedValues) {
      const currentDatasets =
        form.getFieldValue(APIClientFormField.datasets) || [];
      const cleanDatasets = currentDatasets.filter(
        (d) =>
          d !== Dataset.CLAY &&
          d !== Dataset.FIRMOGRAPHICS &&
          d !== Dataset.FIRMOGRAPHICS_IDS
      );

      let newDatasets: Dataset[];

      if (changedValues.application === ApiApplication.API_APPLICATION_CLAY) {
        newDatasets = [Dataset.CLAY];
      } else if (
        changedValues.application === ApiApplication.API_APPLICATION_DATAFEEDS
      ) {
        newDatasets = [
          ...cleanDatasets,
          Dataset.FIRMOGRAPHICS,
          Dataset.FIRMOGRAPHICS_IDS,
        ];
      } else {
        newDatasets = [...cleanDatasets, Dataset.FIRMOGRAPHICS];
      }

      form.setFieldsValue({
        [APIClientFormField.datasets]: newDatasets,
      });
    }
  };

export const CreateClientButton = ({
  className,
  idTeam,
  name,
  initialCredits,
  disabled,
  preselectedApplication,
}: {
  idTeam: number;
  name: string;
  className?: string;
  initialCredits?: number;
  disabled?: boolean;
  preselectedApplication?: ApiApplication;
}): React.ReactElement => {
  const [form] = Form.useForm<APIClientForm>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openSecretModal, setOpenSecretModal] = useState(false);
  const queryClient = useQueryClient();
  const createClient = useCreateClient();
  const addCredits = useAddCredits();
  const watchedApplication = Form.useWatch(
    [APIClientFormField.application],
    form
  );

  const closeModal = () => {
    form.resetFields();
    setIsModalOpen(false);
  };

  const closeSecretModal = () => {
    setOpenSecretModal(false);
    createClient.reset();
  };
  useEffect(() => {
    if (createClient.isSuccess) {
      const apiApp = createClient?.data?.api_application;
      const keepModalOpenAfterCreation =
        createClient?.data?.client_id &&
        (apiApp === ApiApplication.API_APPLICATION_UNSPECIFIED ||
          apiApp === ApiApplication.API_APPLICATION_CLAY);
      setOpenSecretModal(keepModalOpenAfterCreation);
    }
  }, [createClient?.data]);

  const hasError = Boolean(createClient.error || addCredits.error);
  const isLoading = createClient.isLoading || addCredits.isLoading;

  const handleSubmit = async (values: APIClientForm) => {
    const idPackageType =
      values.includeV2API && !values.packageType
        ? IdPackageType.V2_ONLY
        : values.packageType;

    if (!idPackageType) {
      notification.error({
        message: 'v1 API package type or v2 API must be selected',
      });
      return;
    }

    const client: Partial<Client> = {
      name: String(idTeam),
      id_package_type: idPackageType,
      expiration_date: values.expirationDate.format(dateFormat),
      datasets: values.datasets,
      scouting_report_allowed_overage: values.srAllowedOverage,
      scouting_report_credits_allocation: values.srCreditsAllocation,
      chat_cbi_credits_allocation: values.chatCBICreditsAllocation,
      chat_cbi_allowed_overage: values.chatCBIAllowedOverage,
      api_application: values.application,
    };

    const apiClient = await createClient.mutateAsync(client);

    setIsModalOpen(false);
    form.resetFields();

    if (!initialCredits) {
      // don't mutate the credit count if the team already has a client
      await addCredits.mutateAsync({
        clientId: apiClient.client_id,
        amount: values.credits,
      });
    }

    await queryClient.invalidateQueries([
      'externalapiservice/ListTeamClients',
      Number(idTeam),
    ]);
  };

  return (
    <>
      <Button
        className={className}
        onClick={() => setIsModalOpen(true)}
        type="primary"
        disabled={disabled}
        icon={disabled ? <LoadingOutlined /> : null}
      >
        New Client
      </Button>

      <Modal
        title={`New API Client for ${name}?`}
        onCancel={closeModal}
        okButtonProps={{ disabled: isLoading, loading: isLoading }}
        footer={
          <>
            <Button onClick={closeModal} type="default" disabled={isLoading}>
              Cancel
            </Button>
            <Button
              onClick={() => form.submit()}
              type="primary"
              disabled={isLoading}
              loading={isLoading}
            >
              OK
            </Button>
          </>
        }
        open={isModalOpen}
      >
        <Form
          layout="vertical"
          form={form}
          initialValues={{
            [APIClientFormField.expirationDate]: oneYearFromNow,
            [APIClientFormField.credits]: initialCredits,
            [APIClientFormField.includeV2API]:
              preselectedApplication ===
              ApiApplication.API_APPLICATION_DATAFEEDS,
            [APIClientFormField.datasets]:
              preselectedApplication ===
              ApiApplication.API_APPLICATION_DATAFEEDS
                ? [Dataset.FIRMOGRAPHICS, Dataset.FIRMOGRAPHICS_IDS]
                : [],
            [APIClientFormField.application]: preselectedApplication,
          }}
          onValuesChange={onFormClientValuesChange(form)}
          onFinish={handleSubmit}
        >
          <PackageTypeRow
            hideV1Options={[watchedApplication, preselectedApplication].some(
              (app) =>
                app !== undefined &&
                app !== ApiApplication.API_APPLICATION_UNSPECIFIED
            )}
            disableV2Check={preselectedApplication !== undefined}
          />
          <InputsRow hasInitialCredits={initialCredits > 0} />
          <ApplicationFormItem
            disableApplication={
              preselectedApplication ===
              ApiApplication.API_APPLICATION_DATAFEEDS
            }
          />
          <DatasetsRow />
          <DatasetConfigRow />
        </Form>
        {hasError && <p className="text-red-600 mt-4">Something went wrong.</p>}
      </Modal>

      <Modal
        title={`Client Created for ${name}`}
        footer={
          <Button onClick={closeSecretModal} type="primary">
            OK
          </Button>
        }
        open={openSecretModal}
      >
        <div className="text-base">
          <p className="font-bold text-red-600">
            Be sure to copy the Client Secret before closing this page!
          </p>
          <p className="font-bold text-red-600">
            It&apos;s not stored in our database.
          </p>
          <p className="font-medium mt-4">Client Secret:</p>
          <Button
            className="flex items-center font-medium w-full mt-1"
            ghost
            icon={<CopyOutlined />}
            onClick={() =>
              navigator.clipboard.writeText(createClient?.data?.client_secret)
            }
            title={createClient?.data?.client_secret}
            type="primary"
          >
            <span className="truncate">
              {createClient?.data?.client_secret}
            </span>
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default CreateClientButton;
